import { useCallback, useEffect, useState } from "react";

const FILM_KEY = "kuisi_fav_films";
const ACTOR_KEY = "kuisi_fav_actors";
const EVENT_NAME = "kuisi-favorites-change";

function readList(key: string): string[] {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter((v) => typeof v === "string") : [];
  } catch {
    return [];
  }
}

function writeList(key: string, list: string[]) {
  try {
    localStorage.setItem(key, JSON.stringify(list));
  } catch {
    /* ignore quota errors */
  }
  window.dispatchEvent(new Event(EVENT_NAME));
}

function toggleInList(key: string, id: string): string[] {
  const list = readList(key);
  const exists = list.includes(id);
  const next = exists ? list.filter((v) => v !== id) : [...list, id];
  writeList(key, next);
  return next;
}

function useSyncList(key: string) {
  const [list, setList] = useState<string[]>(() => readList(key));

  useEffect(() => {
    const handler = () => setList(readList(key));
    window.addEventListener(EVENT_NAME, handler);
    window.addEventListener("storage", handler);
    return () => {
      window.removeEventListener(EVENT_NAME, handler);
      window.removeEventListener("storage", handler);
    };
  }, [key]);

  const toggle = useCallback(
    (id: string) => {
      setList(toggleInList(key, id));
    },
    [key],
  );

  return { list, toggle };
}

export function useFavorites() {
  const { list: filmList, toggle: toggleFilm } = useSyncList(FILM_KEY);
  const { list: actorList, toggle: toggleActor } = useSyncList(ACTOR_KEY);

  return {
    favoriteFilms: filmList,
    favoriteActors: actorList,
    isFilmFavorite: (id: string) => filmList.includes(id),
    isActorFavorite: (id: string) => actorList.includes(id),
    toggleFilm,
    toggleActor,
  };
}